import { useForm } from "react-hook-form"

import {
    Button,
    EButtonVariant,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { buildCssClassName } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { LocalStorageServiceKeys, StorageService } from "@/services"
import Card from "@/shared/card/Card"
import CheckBox from "@/shared/checkbox/CheckBoxControlled"
import Modal from "@/shared/modal"
import AttentionNotificationBox from "@/shared/notification/AttentionNotificationBox"

import { isEnabledArchiveModal } from "../utils/functions"

import "./styles.scss"

type Props = {
    isOpen: boolean
    archiveText: string
    archiveTitle: string
    onClose: () => void
    onArchive: () => void
}

const storageService: StorageService = new StorageService()

export const ArchiveModal = ({ isOpen, archiveText, archiveTitle, onClose, onArchive }: Props) => {
    const { t }: IUseTranslation = useTranslation()
    const { control, watch } = useForm()

    const popUpCheckBox = watch("show-popup-checkbox")

    const onArhiveClick = () => {
        if (popUpCheckBox) {
            storageService.setItem<string>(LocalStorageServiceKeys.ArchiveModalVisible(), "show")
        }

        onArchive()
    }

    return (
        <Modal
            onClose={onClose}
            show={isOpen && isEnabledArchiveModal(storageService)}
            headerContent={
                <Typography
                    text={t("Archive Entry")}
                    className="p-3 m-0"
                    color={ETypographyColor.Black}
                    weight={ETypographyWeight.Medium}
                    family={ETypographyFamily.DmSerif}
                    alignment={ETypographyAlignment.Center}
                    size={ETypographySize.Heading2}
                    tag={ETypographyTag.Heading2}
                />
            }
            modalBodyClass="archive-modal"
            modalBodyAriaLabel={t("participantSide.coachPlanPage.modals.archiveEntry.ariaLabel")}
            enableOutSideClick
            closeOnEscapePress
        >
            <div className={buildCssClassName("archive-modal__body position-relative")}>
                <AttentionNotificationBox className={buildCssClassName("mb-[40px] attention-banner")}>
                    <p className="m-0 text-gray-100">
                        {t("Are you sure you want to archive this {{archiveTitle}} ?", { archiveTitle })}
                    </p>
                </AttentionNotificationBox>

                <div className="card-example mb-[40px]">
                    <Card readOnly>
                        <p className="m-0 text-gray-100">{archiveText}</p>
                    </Card>
                </div>

                <p className="mb-[40px] text-center text-gray-200 font-italic">
                    {t(
                        "Once this entry is archived it will be hidden from the default view, but you will be able to access it and / or unarchive it as needed it by using the “View” dropdown."
                    )}
                </p>

                <div className="flex justify-center mt-[40px] mb-3">
                    <Button
                        onClick={onArhiveClick}
                        className='"mr-0 mb-2 mr-md-3 mb-md-0 fs-20 font-extrabold scaled-contrasted-text"'
                    >
                        {t("Archive")}
                    </Button>
                    <Button variant={EButtonVariant.Outlined} onClick={onClose}>
                        {t("Cancel")}
                    </Button>
                </div>

                <div className="d-flex justify-content-center align-items-center">
                    <CheckBox
                        control={control}
                        name="show-popup-checkbox"
                        label={t("Don’t show me this pop-up again.")}
                    />
                </div>
            </div>
        </Modal>
    )
}
